import { TWELVE_HOUR_TIME_PARSE_FORMAT } from 'features/store/constants';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { isStoreClosingWithinMinutes, readableCloseHourToday } from 'features/store/utils';
import { useIntl } from 'react-intl';

import { IRestaurant } from '@rbi-ctg/store';
import { ServiceMode } from 'state/service-mode';
import { getConfigValue } from 'utils/environment';

const getOperatingHours = (restaurant: IRestaurant) => (serviceMode: ServiceMode) => {
  // TODO: replace what's below with getStoreOperatingHoursForServiceMode from src/utils/restaurant/index.ts
  switch (serviceMode) {
    case ServiceMode.DRIVE_THRU:
      return restaurant.driveThruHours;
    case ServiceMode.MOBILE_ORDER_DRIVE_THRU:
      return restaurant.mobileOrderDriveThruHours;
    case ServiceMode.CURBSIDE:
      return restaurant.curbsideHours;
    default:
      return restaurant.diningRoomHours;
  }
};

interface IOpenClosedText {
  isOpen: boolean;
  isWithinOneHourOfClose: boolean;
  text: string;
}

export const useOpenClosedText = (store: IRestaurant): IOpenClosedText => {
  const timeFormatConfig = getConfigValue({
    key: 'timeFormat',
    defaultValue: TWELVE_HOUR_TIME_PARSE_FORMAT,
  });
  const { getPickupAvailableServiceModes } = useStoreAvailabilityFns();
  const { formatMessage } = useIntl();

  // Get the open hours for the first available pickup service mode.
  const [operatingHours] = getPickupAvailableServiceModes(store).map(getOperatingHours(store));

  if (!operatingHours) {
    return { isOpen: false, isWithinOneHourOfClose: false, text: formatMessage({ id: 'closed' }) };
  }

  const closesWithinOneHour = isStoreClosingWithinMinutes(store, 60);

  const text = closesWithinOneHour
    ? formatMessage(
        { id: 'closesAt' },
        {
          time: readableCloseHourToday(store, timeFormatConfig),
        }
      )
    : formatMessage({ id: 'open' });

  return {
    isOpen: true,
    isWithinOneHourOfClose: closesWithinOneHour,
    text,
  };
};
